import React, { useState } from "react";
import { Segmented, Upload, UploadFile } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import ImgCrop from "antd-img-crop";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_CROP_RATIO,
  IMAGE_RATIOS,
  ImageRatio,
  ratioValue,
} from "../../../blocks";

import styles from "./index.module.scss";

interface ImageFieldProps {
  /** The picture as a data URL, or undefined while the block has none. */
  value?: string;
  onChange: (image: string | undefined) => void;
  /**
   * The shape the block shows it in, when the block has one. An Image block
   * shows the file at its own shape, so the crop window is where that shape is
   * decided; a block that fixes one has it decided there too, but the choice
   * belongs to the block and is handed back.
   */
  ratio?: ImageRatio;
  /** Given with `ratio`: the block's shape is changed from the crop window. */
  onRatioChange?: (ratio: ImageRatio) => void;
}

/**
 * Where a section's picture is put in, cropped, replaced and taken out again.
 *
 * antd's own picture-card Upload, wrapped in the ImgCrop this project already
 * uses in `Common/ImageUpload`. The crop window is where the picture's shape is
 * decided, from a short list of the shapes a letter uses — not the library's
 * aspect slider, which offers every hundredth between 0.5 and 2 and names them
 * as bare numbers. What the panel decides is how wide the result sits in the
 * letter.
 *
 * The list is put in through antd's own Modal `footer` render function, which
 * hands back the standard Cancel and OK and lets something sit above them. That
 * is the only way in: the modal is the library's, and its body takes no
 * children of ours.
 *
 * It is there in every crop window. Where the block fixes its own shape the row
 * offers that block's shapes and changes the block; where it does not, it
 * offers the letter's and only cuts. Hiding it in the first case left the
 * window looking as though a setting had gone missing.
 *
 * TODO(design): nothing is uploaded. `beforeUpload` returns false and the file
 * is read in the browser, so the picture lives on the block as a data URL until
 * there is an endpoint to put it. See DEVELOPER_TASKS.md.
 */
export default function ImageField({
  value,
  onChange,
  ratio,
  onRatioChange,
}: ImageFieldProps) {
  const { t } = useTranslation();

  // Only for a block that fixes no shape of its own. The prop is read back on
  // every render by ImgCrop, so setting it here moves the crop frame while the
  // window is open.
  const [aspect, setAspect] = useState<number>(ratioValue(DEFAULT_CROP_RATIO));

  const fileList: UploadFile[] = value
    ? [{ uid: "image", name: "image", status: "done", url: value }]
    : [];

  // The same row either way, over two different things. A block that fixes its
  // own shape has the crop window change that shape — the panel's Ratio field
  // and this are one value, not two — and one that does not cuts to whichever
  // of the letter's shapes the picture suits.
  const ratios = ratio
    ? {
        value: ratio as string,
        options: IMAGE_RATIOS.map((name) => ({ value: name, label: name })),
        onChange: (next: string | number) =>
          onRatioChange?.(next as ImageRatio),
      }
    : {
        value: aspect as number,
        options: IMAGE_RATIOS.map((name) => ({
          value: ratioValue(name),
          label: name,
        })),
        onChange: (next: string | number) => setAspect(next as number),
      };

  return (
    <div
      className={styles.field}
      // The shape the tile is drawn in: the block's own where it fixes one, a
      // 2:1 band otherwise. The stylesheet reads it from here — it had been
      // falling back to its default since the crop rework took the property
      // away, so an Image + text tile stayed 2:1 whatever the block was set to.
      style={
        {
          "--tile-aspect": ratio
            ? ratioValue(ratio)
            : ratioValue(DEFAULT_CROP_RATIO),
        } as React.CSSProperties
      }
    >
      <ImgCrop
        // The block's own shape wins where it has one: what is cut there is
        // what the letter shows beside the text, so the two cannot disagree.
        aspect={ratio ? ratioValue(ratio) : aspect}
        rotationSlider
        showReset
        resetText={t("common.reset")}
        modalTitle={t("pages.campaigns.builder.cropImage")}
        modalProps={{
          footer: (originNode: React.ReactNode) => (
            <div className={styles.cropFooter}>
              <Segmented
                className={styles.cropRatios}
                // The row has no room for a label beside it, and the shapes are
                // read off the options themselves.
                aria-label={t("pages.campaigns.builder.ratio")}
                value={ratios.value}
                onChange={ratios.onChange}
                options={ratios.options}
              />
              <div className={styles.cropButtons}>{originNode}</div>
            </div>
          ),
        }}
        onModalOk={(file) => {
          if (
            !file ||
            typeof file === "string" ||
            typeof file === "boolean" ||
            file instanceof Promise
          )
            return;

          const reader = new FileReader();

          reader.onload = () => onChange(String(reader.result));
          reader.readAsDataURL(file);
        }}
      >
        <Upload
          className={styles.upload}
          accept="image/*"
          listType="picture-card"
          maxCount={1}
          fileList={fileList}
          // Nothing is posted: the cropper hands the file back and it is read
          // here, so returning false is what keeps antd from sending it.
          beforeUpload={() => false}
          onRemove={() => onChange(undefined)}
        >
          {value ? null : (
            <span className={styles.trigger}>
              <PlusOutlined />
              {t("pages.campaigns.builder.uploadImage")}
            </span>
          )}
        </Upload>
      </ImgCrop>
    </div>
  );
}
